import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import t from 'react-translate';
import ClickableContainer from 'components/clickable-container';
import NvRoundButton from 'shared/components/nv-round-button';
import NvTooltip from 'shared/components/nv-tooltip';
import { config } from '@config/pendo.config.json';
import { AdminUser } from 'redux/schemas/models/help-options';
import { getCourseAliases } from 'redux/selectors/course';
import { tagButtonFieldStyles } from './styles';
import TagListAutocomplete from './tag-list-autocomplete';

export enum TagButtonType {
  Normal,
  Compact,
}

const MAX_TAGS = 5;

interface TagButtonFieldProps {
  type: TagButtonType;
  onAdd: (id: number) => void;
  disabled?: boolean;
  items: AdminUser[];
  selectedTags: AdminUser[];
}

const TagButtonField: React.FC<TagButtonFieldProps> = ({
  items,
  selectedTags = [],
  type,
  onAdd,
  disabled = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const aliases = useSelector(getCourseAliases);
  // Use appropriate label based on button type
  const label = type === TagButtonType.Compact ? '' : t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.ADD_BUTTON();

  useEffect(() => {
    setEditing(false);
  }, [items]);

  const onClick = () => {
    setEditing(true);
  };

  const onCancel = () => {
    setEditing(false);
  };

  const onMouseEnter = () => {
    setShowTooltip(disabled);
  };

  const onMouseLeave = () => {
    setShowTooltip(false);
  };

  const buttonComponent = (
    <NvRoundButton
      css={tagButtonFieldStyles}
      icon={`icon-create-new-post ${type === TagButtonType.Normal ? 'normal' : ''}`}
      label={label}
      onClick={onClick}
      className={`small-size ${type === TagButtonType.Normal && !disabled ? 'normal' : 'compact'}`}
      disabled={disabled}
      ariaLabel={label}
      dataQa={config.pendo.aiLearning.addAdminAssistant}
    />
  );

  return (
    <>
      {disabled && (
        <NvTooltip
          show={showTooltip}
          text={
          selectedTags.length === MAX_TAGS ? t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.LIMITED_TEACHING_TEAM(aliases?.teachingTeamAliases)
            : t.INSTITUTIONS.ADVANCED_SETTINGS.HELP_OPTIONS.AI_ASSISTANT.LIMITED_TEACHING_TEAM_DISABLED()
          }
        >
          <ClickableContainer
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            data-qa={config.pendo.aiLearning.addAdminAssistant}
            onClick={() => setShowTooltip(!showTooltip)}
          >
            {buttonComponent}
          </ClickableContainer>
        </NvTooltip>
      )}


      {!editing && !disabled && buttonComponent}

      {editing && (
        <TagListAutocomplete
          items={items}
          onCancel={onCancel}
          onAdd={onAdd}
        />
      )}
    </>
  );
};

export default TagButtonField;
